import { DeviceType, Layout } from '@repo/types';

type PageLayouts = {
  mobileLayout?: Layout;
  tabletLayout?: Layout;
  desktopLayout?: Layout;
};

type LayoutKey = keyof PageLayouts;

/**
 * Which stored layouts a device reads, in fallback order.
 *
 * Desktop deliberately has no fallback: a page with only a mobile layout authored should
 * render nothing on desktop rather than silently serving a phone layout to a widescreen
 * visitor. That asymmetry is inherited from the original inline `switch` and is preserved
 * here exactly.
 */
const LAYOUT_FALLBACKS: Partial<Record<DeviceType, readonly LayoutKey[]>> = {
  [DeviceType.mobile]: ['mobileLayout', 'tabletLayout', 'desktopLayout'],
  [DeviceType.tablet]: ['tabletLayout', 'mobileLayout', 'desktopLayout'],
  [DeviceType.desktop]: ['desktopLayout'],
};

/**
 * Resolve the layout to render for a device.
 *
 * Each field is resolved **independently** down the fallback list rather than picking one
 * layout wholesale — a layout that exists but omits a field still falls through to the
 * next. That mirrors the per-field `??` chains this replaced; picking a single layout
 * first would be a subtle behaviour change on partially-authored pages.
 *
 * Extracted from `CmsPage` so it is testable at all: it previously lived inline in a
 * `useMemo` with no coverage, and four near-identical branches.
 */
export const selectDeviceLayout = (
  pageData: PageLayouts | undefined,
  deviceType: DeviceType | undefined
): Layout | null => {
  const order = deviceType ? LAYOUT_FALLBACKS[deviceType] : undefined;
  if (!pageData || !order) {
    return null;
  }

  const pick = <K extends keyof Layout>(field: K): Layout[K] | undefined =>
    order.map((name) => pageData[name]?.[field]).find((value) => value !== undefined && value !== null);

  const rootChildren = pick('rootChildren');
  // `GeneratePage` maps over `rootChildren` immediately, so an absent one is not a layout
  // to render — it is "this device has no layout". Returning null rather than a partial
  // object makes the caller handle that explicitly; the previous inline version passed
  // `undefined` straight through and `GeneratePage` threw on `.map`.
  if (!rootChildren) {
    return null;
  }

  return {
    rootChildren,
    rows: pick('rows') ?? {},
    columns: pick('columns') ?? {},
    components: pick('components') ?? {},
  };
};
